import { Operator } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import type { PlainMessage } from '@bufbuild/protobuf';
import { IDropdownOption, IStyle, Label, mergeStyles, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesStealth,
  Dropdown,
  IH2OTheme,
  Info,
  KeyValuePairEditor,
  Link,
  Loader,
  Search,
  Slider,
  useClassNames,
  useTheme,
  useToast,
  WidgetItem,
} from '@h2oai/ui-kit';
import React from 'react';
import { useHistory } from 'react-router-dom';
import semver from 'semver';

import { FALLBACK_MLOPS_API_VERSION, useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { ArtifactComposition } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/artifact_composition_pb';
import { DeploymentComposition } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_composition_pb';
import { useMLOpsService } from '../../mlops/hooks';
import { useQueryKubernetesDeploymentMaxReplicas } from '../../mlops/mlops.deployer.hooks';
import { listModelVersionsForModel } from '../../mlops/mlops.storage.functions';
import { useQueryRegisteredModels } from '../../mlops/mlops.storage.hooks';
import { useInfiniteQueryFetchAllPages } from '../../services/hooks';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { formatError } from '../../utils/utils';
// TODO: Move to global context.
import { WidgetIconButton } from '../Orchestrator/Runnables';
import { FORM_SECTION_MAX_WIDTH } from './BatchScoringDetail';
import { ROUTES } from './constants';
import { countSelectedModels, DeploymentTypeKey, DropdownOption } from './DeploymentTabDetail';
import { DeploymentOptions, RegisteredModelItem } from './Models';
import type { RegisteredModelVersionWithTag } from './ModelTaggingWidget';
import { useGranularMLOpsPermissions } from './userActions';

export type RegisteredModelVersions = Record<string, RegisteredModelVersionWithTag[]>;

interface IModelSelectorStyles {
  placeholderContainer: IStyle;
  placeholderContainerSmall: IStyle;
  placeholderContent: IStyle;
  placeholderContentSmall: IStyle;
  widgetLayout: IStyle;
  loader: IStyle;
  errorText: IStyle;
  sliderLabel: IStyle;
  sliderLabelLeft: IStyle;
  sliderLabelRight: IStyle;
  formTitle: IStyle;
}

const modelSelectorStyles = (theme: IH2OTheme): Partial<IModelSelectorStyles> => ({
    placeholderContainer: {
      margin: '16px 0',
      height: 100, // Widget height.
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      outlineWidth: 1,
      outlineStyle: 'dashed',
      outlineColor: theme.semanticColors?.inputBorder,
    },
    placeholderContainerSmall: {
      width: '50%',
    },
    placeholderContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 452, // Callout width.
    },
    placeholderContentSmall: {
      minWidth: 280, // Callout width.
    },
    widgetLayout: {
      display: 'flex',
      gap: 16,
    },
    loader: {
      display: 'flex',
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
      height: 100, // Widget height.
    },
    errorText: {
      color: theme.semanticColors?.inputErrorMessageText,
    },
    sliderLabel: { display: 'flex', maxWidth: 460 },
    sliderLabelLeft: { flex: '1 1 0%', color: 'var(--h2o-gray500, #6B7280)', fontSize: 12 },
    sliderLabelRight: { flex: '1 1 0%', color: 'var(--h2o-gray500, #6B7280)', fontSize: 12, textAlign: 'right' },
    formTitle: { marginBottom: 8 },
  }),
  widgetItemStyles = (theme: IH2OTheme) => ({
    root: {
      display: 'flex',
      alignItems: 'center',
      borderRadius: 8,
      margin: '16px 0',
      height: 100,
      outlineWidth: 1,
      outlineStyle: 'solid',
      outlineColor: theme.semanticColors?.inputBorder,
      '.h2o-WidgetItem-actions': {
        flex: 3,
      },
      '.h2o-WidgetItem-content': {
        flex: 1,
      },
    },
  }),
  versionDropdownStyles = {
    root: {
      width: 58,
      marginRight: 8,
      marginLeft: 4,
    },
    title: {
      border: 'none',
      borderBottom: '1px solid black',
      borderRadius: 0,
      padding: 0,
      fontSize: 14,
      backgroundColor: 'transparent',
      lineHeight: 'unset',
      minHeight: 'unset',
    },
    caretDownWrapper: {
      lineHeight: 'unset',
      right: 4,
      i: {
        fontSize: 12,
      },
    },
  },
  actionStyle: React.CSSProperties = {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
    alignContent: 'center',
    flexWrap: 'wrap',
  },
  sliderContainerStyles = {
    root: {
      maxWidth: 460,
    },
    minMaxLabel: {
      display: 'none',
    },
    input: {
      display: 'none',
    },
  },
  replicasSliderContainerStyles = {
    root: {
      maxWidth: 460,
    },
  },
  getWidgetActions = (
    onOpenSelection: () => void,
    onRemoveSelection: () => void,
    onAddNewVersion: () => void,
    classNames: ClassNamesFromIStyles<IModelSelectorStyles>,
    onSelectVersion: (experimentId: string) => void,
    versionOptions: IDropdownOption[],
    selectedVersionExperimentId?: string,
    readOnly?: boolean
  ) => (
    <div style={{ display: 'flex', flexGrow: 1 }}>
      {selectedVersionExperimentId && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>Model version</div>
          <Dropdown
            options={versionOptions}
            styles={versionDropdownStyles}
            selectedKey={selectedVersionExperimentId}
            onChange={(_, option) => onSelectVersion(option?.key as string)}
            disabled={readOnly || !versionOptions.length}
          />
        </div>
      )}
      {!versionOptions.length ? (
        <p className={classNames.errorText} style={{ marginLeft: 6, padding: 4 }}>
          This model has no versions.{' '}
          <button
            onClick={onAddNewVersion}
            style={{
              background: 'none',
              border: 'none',
              // TODO: Theme this.
              color: 'var(--h2o-red500, #BA2525)',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 14,
              padding: 0,
            }}
          >
            Add version
          </button>{' '}
          to this model or select a different model.
        </p>
      ) : null}
      {!readOnly ? (
        <div style={actionStyle}>
          <WidgetIconButton iconName="OpenInNewTab" onClick={onOpenSelection} title="Open in new tab" />
          <WidgetIconButton
            title="Delete"
            iconName="Delete"
            iconColor="white"
            backgroundColor="red"
            hasBorder={false}
            onClick={onRemoveSelection}
          />
        </div>
      ) : null}
    </div>
  ),
  defaultWidgetData = {
    title: `Selected item does not exist.`,
    description: '',
    iconName: '',
  };

type ResourceSpec = { [key: string]: string };

const getMaxSpec = (spec1: ResourceSpec, spec2: ResourceSpec): ResourceSpec => {
  if (!spec1 && !spec2) return {};
  const allKeys = new Set([...Object.keys(spec1 || {}), ...Object.keys(spec2 || {})]);

  const parseResourceValue = (value: string | undefined): number => {
    if (!value) return 0;

    const memoryMatch = value.match(/^(\d+(?:\.\d+)?)([KMG]i)?$/i);
    if (memoryMatch) {
      const num = parseFloat(memoryMatch[1]);
      const unit = memoryMatch[2]?.toLowerCase();
      const factor = unit === 'ki' ? 1 : unit === 'mi' ? 1024 : unit === 'gi' ? 1024 * 1024 : 1;
      return num * factor;
    }

    // Default to float for values like "0.5" (e.g., CPU)
    return parseFloat(value);
  };

  const result: ResourceSpec = {};

  for (const key of allKeys) {
    const val1 = spec1[key];
    const val2 = spec2[key];

    result[key] = parseResourceValue(val2) > parseResourceValue(val1) ? val2 : val1;
  }

  return result;
};

const buttonRootHoverStyleRemove = {
  backgroundColor: 'transparent',
  borderColor: 'transparent',
};

export const Expander = ({
  onChangeExpanded,
  label,
}: {
  onChangeExpanded: (isExpanded: boolean) => void;
  label: string;
}) => {
  const theme = useTheme();

  return (
    <div
      style={{ cursor: 'pointer' }}
      onClick={(ev) => {
        const icon = (ev.currentTarget as HTMLElement)?.querySelector('.ms-Icon');
        if (icon) {
          onChangeExpanded(!icon.classList?.contains('rotate90'));
          icon.classList.toggle('rotate90');
        }
      }}
    >
      <hr
        style={{
          border: 'none',
          borderTop: `1px solid ${theme.semanticColors?.inputBorder}`,
          marginTop: 20,
          marginBottom: 0,
        }}
      />
      <Button
        iconName="ChevronRight"
        text={label}
        styles={[
          buttonStylesStealth,
          {
            icon: {
              fontSize: 14,
              transitionDuration: '300ms',
              transitionProperty: 'transform',
            },
            root: {
              marginTop: 10,
              marginBottom: 10,
              '.ms-Icon.rotate90': {
                transform: 'rotate(90deg)',
              },
              backgroundColor: 'transparent',
            },
            rootHovered: buttonRootHoverStyleRemove,
            rootFocused: buttonRootHoverStyleRemove,
            rootPressed: buttonRootHoverStyleRemove,
          },
        ]}
      />
      <hr
        style={{
          border: 'none',
          borderTop: `1px solid ${theme.semanticColors?.inputBorder}`,
          marginTop: 0,
          marginBottom: 20,
        }}
      />
    </div>
  );
};

export const TooltipLabel = ({
  label,
  tooltipText,
  hasTopSpacing = true,
  isBoldLabel = false,
}: {
  label: string;
  tooltipText: string;
  hasTopSpacing?: boolean;
  isBoldLabel?: boolean;
}) => (
  <div style={{ display: 'flex', alignItems: 'center', marginTop: hasTopSpacing ? 16 : 0 }}>
    <Label styles={{ root: { marginRight: 8, fontSize: 14, fontWeight: isBoldLabel ? 'bold' : undefined } }}>
      {label}
    </Label>
    <Info
      styles={{ text: { alignContent: 'center' } }}
      tooltipHostProps={{
        styles: {
          root: {
            display: 'flex',
            alignItems: 'center',
          },
        },
      }}
      isTooltip
    >
      {tooltipText}
    </Info>
  </div>
);

const ModelSelector = ({
  selectedModels,
  onSelectedModelsChange,
  defaultDeploymentCompositionA,
  defaultDeploymentCompositionB,
  trafficDistribution = 50,
  onTrafficDistributionChange,
  deploymentType,
  onChangeOptions,
  defaultSelectedVersionId,
  defaultSelectedModelIds,
  defaultSelectedExperimentIds,
  defaultRequestsConfig,
  defaultLimitsConfig,
  defaultReplicas,
  defaultEnvVariablesConfig,
  defaultAffinity = 'none',
  defaultToleration = 'none',
  onAffinityChange,
  onTolerationChange,
  onContentLoadFinish,
  onContentLoadStart,
  readOnly,
  isNew,
  workspaceId,
  onChangeModelSelectorAdvancedExpanded,
}: {
  selectedModels: (PlainMessage<RegisteredModel> | undefined)[];
  onSelectedModelsChange: (models: (PlainMessage<RegisteredModel> | undefined)[]) => void;
  defaultDeploymentCompositionA?: DeploymentComposition;
  defaultDeploymentCompositionB?: DeploymentComposition;
  trafficDistribution?: number;
  onTrafficDistributionChange?: (value?: number) => void;
  deploymentType: DeploymentTypeKey;
  onChangeOptions: (options: DeploymentOptions) => void;
  defaultSelectedVersionId?: string;
  defaultSelectedModelIds?: string[];
  defaultSelectedExperimentIds?: string[];
  defaultRequestsConfig?: ResourceSpec;
  defaultLimitsConfig?: ResourceSpec;
  defaultReplicas?: number;
  defaultEnvVariablesConfig?: Record<string, string>;
  defaultAffinity?: string;
  defaultToleration?: string;
  onAffinityChange: (key: string) => void;
  onTolerationChange: (key: string) => void;
  onContentLoadFinish?: () => void;
  onContentLoadStart?: () => void;
  readOnly?: boolean;
  isNew?: boolean;
  workspaceId: string;
  onChangeModelSelectorAdvancedExpanded?: (isExpanded: boolean) => void;
}) => {
  const theme = useTheme(),
    classNames = useClassNames<IModelSelectorStyles, ClassNamesFromIStyles<IModelSelectorStyles>>(
      'modelSelectorStyles',
      modelSelectorStyles(theme)
    ),
    { addToast } = useToast(),
    history = useHistory(),
    workspaceName = React.useMemo(() => `workspaces/${workspaceId}`, [workspaceId]),
    permissions = useGranularMLOpsPermissions(workspaceName),
    shouldShowTwoWidgets = deploymentType === 'ab_test' || deploymentType === 'champion_challenger',
    [artifactCompositionOptionsA, setArtifactCompositionOptionsA] = React.useState<DropdownOption[]>([]),
    [selectedArtifactCompositionA, setSelectedArtifactCompositionA] = React.useState<string>(),
    [artifactCompositionOptionsB, setArtifactCompositionOptionsB] = React.useState<DropdownOption[]>([]),
    [selectedArtifactCompositionB, setSelectedArtifactCompositionB] = React.useState<string>(),
    mlopsService = useMLOpsService(),
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    calloutARef = React.useRef<HTMLDivElement | null>(null),
    calloutBRef = React.useRef<HTMLDivElement | null>(null),
    [requestsConfig, setRequestsConfig] = React.useState<ResourceSpec>(defaultRequestsConfig || {}),
    [limitsConfig, setLimitsConfig] = React.useState<ResourceSpec>(defaultLimitsConfig || {}),
    [maxReplicas, setMaxReplicas] = React.useState<number | undefined>(10),
    [replicas, setReplicas] = React.useState<number>(defaultReplicas !== undefined ? defaultReplicas : 1),
    [envVariablesConfig, setEnvVariablesConfig] = React.useState<Record<string, string>>(
      defaultEnvVariablesConfig || {}
    ),
    [affinity, setAffinity] = React.useState<string>(defaultAffinity),
    [toleration, setToleration] = React.useState<string>(defaultToleration),
    [kubernetesConfigurationShortcutsOptions, setKubernetesConfigurationShortcutsOptions] = React.useState<{
      affinity: IDropdownOption[];
      toleration: IDropdownOption[];
    }>(),
    [searchFilter, setSearchFilter] = React.useState<string>(),
    [registeredModelVersions, setRegisteredModelVersions] = React.useState<RegisteredModelVersions>(),
    [firstSelectedModelVersionOptions, setFirstSelectedModelVersionOptions] = React.useState<IDropdownOption[]>([]),
    [secondSelectedModelVersionOptions, setSecondSelectedModelVersionOptions] = React.useState<IDropdownOption[]>([]),
    [firstSelectedModelVersionExperimentId, setFirstSelectedModelVersionExperimentId] = React.useState(
      defaultSelectedExperimentIds?.[0]
    ),
    [secondSelectedModelVersionExperimentId, setSecondSelectedModelVersionExperimentId] = React.useState(
      defaultSelectedExperimentIds?.[1]
    ),
    [isLoading, setIsLoading] = React.useState(true),
    [isAdvancedModelsExpanded, setAdvancedModelsExpanded] = React.useState(false),
    shouldFetchRegisteredModels = !!workspaceId && permissions?.canListModels;

  const registeredModelsQuery = useQueryRegisteredModels(
    searchFilter
      ? {
          projectId: workspaceId,
          paging: {
            pageSize: 20,
            pageToken: undefined as any, // pageToken is wrongly required in the generated proto
          },
          filter: {
            query: {
              clause: [
                {
                  propertyConstraint: [
                    {
                      property: {
                        propertyType: {
                          value: 'name',
                          case: 'field',
                        },
                      },
                      operator: Operator.ICONTAINS,
                      value: {
                        value: {
                          case: 'stringValue',
                          value: searchFilter,
                        },
                      },
                    },
                  ],
                  tagConstraint: [],
                },
              ],
            },
          },
        }
      : {
          projectId: workspaceId,
          paging: {
            pageSize: 20,
            pageToken: undefined as any, // pageToken is wrongly required in the generated proto
          },
        },
    {
      enabled: shouldFetchRegisteredModels,
      select: (data) => {
        const flattenedModels = data.pages.flatMap((page) => page.registeredModels || []);
        return { pages: flattenedModels, pageParams: data.pageParams };
      },
    }
  );

  useInfiniteQueryFetchAllPages(registeredModelsQuery);

  const registeredModelItems = React.useMemo(() => {
    if (!registeredModelsQuery.data?.pages) return undefined;
    return registeredModelsQuery.data.pages.map((model) => ({
      ...model,
      // TODO: Display user name instead of the id.
      createdByName: model.createdBy,
    })) as RegisteredModelItem[];
  }, [registeredModelsQuery.data?.pages]);

  const mlopsApiVersionQuery = useQueryMlopsApiVersion();
  const mlopsApiVersion = mlopsApiVersionQuery.data || FALLBACK_MLOPS_API_VERSION;
  const isMlopsApiV1_0_x = semver.satisfies(mlopsApiVersion, '1.0.x');

  const kubernetesMaxReplicasQuery = useQueryKubernetesDeploymentMaxReplicas({
      select: (data) => data.maxReplicas,
      onSuccess: (data) => {
        const newMaxReplicas = data !== undefined ? data : undefined;
        setMaxReplicas(newMaxReplicas);

        if (defaultReplicas === undefined && newMaxReplicas !== undefined && replicas > newMaxReplicas) {
          setReplicas(newMaxReplicas);
        }
      },
      onError: (err) => {
        const message = `Failed to fetch Kubernetes deployment max replicas: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      },
      enabled: !isMlopsApiV1_0_x,
    }),
    loadStateRef = React.useRef({
      getRegisteredModelVersion: false,
      fetchEntityArtifacts: false,
      fetchingScoringCompositions: false,
      fetchingSuggestedScoringResourceRequirements: false,
      fetchingKubernetesConfigurationShortcuts: false,
      fetchingKubernetesDeploymentMaxReplicas: !isMlopsApiV1_0_x,
      fetchingRegisteredModels: shouldFetchRegisteredModels,
    }),
    // Used by quick deploy action for one-time setting of model selection.
    isDefaultSelectionInitializedRef = React.useRef({
      isModelVersionInitialized: false,
      isModelSelectionInitialized: false,
    }),
    evaluateLoading = React.useCallback(() => {
      // TODO: Remove loading refs regarding ModelSelector.
      if (
        !loadStateRef.current.getRegisteredModelVersion &&
        !loadStateRef.current.fetchEntityArtifacts &&
        !loadStateRef.current.fetchingScoringCompositions &&
        !loadStateRef.current.fetchingSuggestedScoringResourceRequirements &&
        !loadStateRef.current.fetchingKubernetesConfigurationShortcuts &&
        !loadStateRef.current.fetchingKubernetesDeploymentMaxReplicas &&
        !loadStateRef.current.fetchingRegisteredModels
      ) {
        setIsLoading(false);
      }
    }, []),
    discoverKubernetesConfigShortcuts = React.useCallback(async () => {
      loadStateRef.current.fetchingKubernetesConfigurationShortcuts = true;
      setIsLoading(true);
      try {
        const response = await mlopsService.discoverKubernetesConfigShortcuts({});
        const kubernetesAffinityShortcuts = response?.kubernetesAffinityShortcuts || [];
        const kubernetesTolerationShortcuts = response?.kubernetesTolerationShortcuts || [];
        setKubernetesConfigurationShortcutsOptions({
          affinity: (kubernetesAffinityShortcuts || []).map((shortcut, idx) => ({
            key: shortcut?.name || `affinity-shortcut-${idx}`, // Ensure key is string
            text: shortcut?.displayName || '(Unknown)',
          })),
          toleration: (kubernetesTolerationShortcuts || []).map((shortcut, idx) => ({
            key: shortcut?.name || `toleration-shortcut-${idx}`, // Ensure key is string
            text: shortcut?.displayName || '(Unknown)',
          })),
        });
      } catch (err) {
        const message = `Failed to fetch Kubernetes configuration shortcuts: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.fetchingKubernetesConfigurationShortcuts = false;
        evaluateLoading();
      }
    }, [mlopsService, addToast]),
    getScoringCompositions = React.useCallback(
      async (experimentId?: string) => {
        loadStateRef.current.fetchingScoringCompositions = true;
        setIsLoading(true);
        try {
          // TODO: Check if parent is correct after v2 migration.
          const response = await mlopsService.listExperimentArtifactCompositions({
            parent: `${workspaceName}/experiments/${experimentId}`,
          });
          const experimentArtifactCompositions: ArtifactComposition[] | undefined =
            response?.experimentArtifactCompositions;
          if (response && !experimentArtifactCompositions) {
            console.error('No scoring compositions found in the response.');
          }
          return experimentArtifactCompositions;
        } catch (err) {
          const message = `Failed to fetch scoring compositions: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.fetchingScoringCompositions = false;
          evaluateLoading();
        }
      },
      [addToast]
    ),
    getSuggestedScoringResourceRequirements = React.useCallback(
      async (experimentId: string, runtimeName: string) => {
        if (!workspaceName) return;
        loadStateRef.current.fetchingSuggestedScoringResourceRequirements = true;
        setIsLoading(true);
        try {
          const response = await mlopsService.computeSuggestedScoringResourceRequirements({
            // TODO: Use experimentId directly when v2 api is available.
            experiment: `${workspaceName}/experiments/${experimentId}`,
            runtime: runtimeName,
          });
          const resourceSpec = response?.kubernetesResourceRequirements;
          if (response && !resourceSpec) {
            console.error('No default runtime Kubernetes resource spec found in the response.');
          }
          return { requestsConfig: resourceSpec?.requests || {}, limitsConfig: resourceSpec?.limits || {} };
        } catch (err) {
          const message = `Failed to fetch default runtime kubernetes resource spec: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.fetchingSuggestedScoringResourceRequirements = false;
          evaluateLoading();
        }
      },
      [mlopsService, workspaceName]
    ),
    computeRequestsAndLimits = React.useCallback(
      async (
        firstModelScoringComposition?: DeploymentComposition,
        secondModelScoringComposition?: DeploymentComposition
      ) => {
        if (firstModelScoringComposition && secondModelScoringComposition) {
          const firstResourceSpec = await getSuggestedScoringResourceRequirements(
            firstModelScoringComposition.experimentId,
            firstModelScoringComposition.runtime
          );
          const secondResourceSpec = await getSuggestedScoringResourceRequirements(
            secondModelScoringComposition.experimentId,
            secondModelScoringComposition.runtime
          );
          if (firstResourceSpec && secondResourceSpec) {
            const maxRequestsConfig =
              getMaxSpec(firstResourceSpec.requestsConfig, secondResourceSpec.requestsConfig) || {};
            // TODO: Create more proper type.
            const maxLimitsConfig = getMaxSpec(firstResourceSpec.limitsConfig, secondResourceSpec.limitsConfig) || {};
            // TODO: Create more proper type.
            return {
              requestsConfig: maxRequestsConfig,
              limitsConfig: maxLimitsConfig,
            };
          }
        } else {
          if (
            (firstModelScoringComposition?.experimentId || secondModelScoringComposition?.experimentId) &&
            (firstModelScoringComposition?.runtime || secondModelScoringComposition?.runtime)
          ) {
            const resourceSpec = await getSuggestedScoringResourceRequirements(
              firstModelScoringComposition?.experimentId || secondModelScoringComposition?.experimentId || '',
              firstModelScoringComposition?.runtime || secondModelScoringComposition?.runtime || ''
            );
            if (resourceSpec) {
              return {
                requestsConfig: resourceSpec.requestsConfig,
                limitsConfig: resourceSpec.limitsConfig,
              };
            }
          }
        }

        return {
          requestsConfig: {},
          limitsConfig: {},
        };
      },
      []
    ),
    getModelVersionsByModel = React.useCallback(
      async (modelId: string, modelName: string) => {
        try {
          const response = await listModelVersionsForModel(mlopsApiUrl, { registeredModelId: modelId });
          const modelVersions = response?.modelVersions;
          if (modelVersions) {
            return modelVersions;
          } else {
            console.error('No model versions found in the response.');
            return undefined;
          }
        } catch (err) {
          const message = `Failed to fetch model versions for ${modelName} (${modelId}): ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        }
      },
      [addToast, mlopsApiUrl]
    ),
    onSelectModelUpdateOptions = React.useCallback(async () => {
      const selectedModelsCount = countSelectedModels(selectedModels);
      if (!selectedModelsCount) return;
      const isFirstModel = selectedModels?.[0] && firstSelectedModelVersionExperimentId;
      const isSecondModel = selectedModels?.[1] && secondSelectedModelVersionExperimentId;

      if (
        deploymentType !== 'single_model' &&
        (selectedModelsCount !== 2 || !firstSelectedModelVersionExperimentId || !secondSelectedModelVersionExperimentId)
      ) {
        return;
      }

      if (isFirstModel) {
        const firstModelScoringCompositions = await getScoringCompositions(firstSelectedModelVersionExperimentId);

        if (!firstModelScoringCompositions) {
          const message = 'No experiment found for the model.';
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return;
        }

        setArtifactCompositionOptionsA(
          firstModelScoringCompositions.map((composition) => ({
            key: `${composition?.deployableArtifactType?.name}-${composition?.runtime?.name}`,
            text: `${composition?.deployableArtifactType?.displayName} (${composition?.runtime?.displayName})`,
            data: composition,
          }))
        );
        if (!defaultDeploymentCompositionA) {
          // Do not select the first composition for already created deployments.
          setSelectedArtifactCompositionA(
            firstModelScoringCompositions[0]
              ? `${firstModelScoringCompositions[0]?.deployableArtifactType?.name}-${firstModelScoringCompositions[0].runtime?.name}`
              : ''
          );
        }
      }

      if (isSecondModel) {
        const secondModelScoringCompositions = await getScoringCompositions(secondSelectedModelVersionExperimentId);

        if (!secondModelScoringCompositions) {
          const message = 'No experiment found for the second model.';
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return;
        }

        setArtifactCompositionOptionsB(
          secondModelScoringCompositions.map((composition) => ({
            key: `${composition?.deployableArtifactType?.name}-${composition.runtime?.name}`,
            text: `${composition?.deployableArtifactType?.displayName} (${composition.runtime?.displayName})`,
            data: composition,
          }))
        );
        if (!defaultDeploymentCompositionB) {
          // Do not select the first composition for already created deployments.
          setSelectedArtifactCompositionB(
            secondModelScoringCompositions[0]
              ? `${secondModelScoringCompositions[0]?.deployableArtifactType?.name}-${secondModelScoringCompositions[0].runtime?.name}`
              : ''
          );
        }
      }
    }, [
      addToast,
      selectedModels,
      firstSelectedModelVersionExperimentId,
      secondSelectedModelVersionExperimentId,
      defaultDeploymentCompositionA,
      defaultDeploymentCompositionB,
      deploymentType,
    ]),
    updateOptions = React.useCallback(
      async (computeRequestsLimits = true) => {
        const selectedModelsCount = countSelectedModels(selectedModels);
        if (!selectedModelsCount) return;
        let firstModelOptions: Partial<DeploymentOptions> = {};
        let secondModelOptions: Partial<DeploymentOptions> = {};
        const isFirstModel = selectedModels?.[0] && firstSelectedModelVersionExperimentId;
        const isSecondModel = selectedModels?.[1] && secondSelectedModelVersionExperimentId;

        if (isFirstModel) {
          const firstModelArtifactComposition = artifactCompositionOptionsA?.find(
            (option) => option.key === selectedArtifactCompositionA
          )?.data as ArtifactComposition;

          // Converts artifact composition to deployment composition as DeploymentComposition is not available from the API.
          const firstModelDeploymentComposition: DeploymentComposition = {
            experimentId: firstSelectedModelVersionExperimentId,
            deployableArtifactType: firstModelArtifactComposition?.deployableArtifactType?.name || '',
            artifactProcessor: firstModelArtifactComposition?.artifactProcessor?.name || '',
            runtime: firstModelArtifactComposition?.runtime?.name || '',
            // TODO: Do not send runtimeImage by default, only if user wants to override it.
            // runtimeImage: firstModelArtifactComposition?.runtime?.image || '',
          };

          firstModelOptions = {
            firstModelDeploymentComposition,
            kubernetesOptions: {
              replicas,
            },
            envVariables: envVariablesConfig,
          };
        }
        if (isSecondModel) {
          const secondModelArtifactComposition = artifactCompositionOptionsB?.find(
            (option) => option.key === selectedArtifactCompositionB
          )?.data as ArtifactComposition;

          // Converts artifact composition to deployment composition as DeploymentComposition is not available from the API.
          const secondModelDeploymentComposition: DeploymentComposition = {
            experimentId: secondSelectedModelVersionExperimentId,
            deployableArtifactType: secondModelArtifactComposition?.deployableArtifactType?.name || '',
            artifactProcessor: secondModelArtifactComposition?.artifactProcessor?.name || '',
            runtime: secondModelArtifactComposition?.runtime?.name || '',
            // TODO: Do not send runtimeImage by default, only if user wants to override it.
            // runtimeImage: secondModelArtifactComposition?.runtime?.image || '',
          };

          secondModelOptions = {
            secondModelDeploymentComposition,
            kubernetesOptions: {
              replicas,
            },
            envVariables: envVariablesConfig,
          };
        }

        const { requestsConfig: requests, limitsConfig: limits } = computeRequestsLimits
          ? await computeRequestsAndLimits(
              firstModelOptions.firstModelDeploymentComposition,
              secondModelOptions.secondModelDeploymentComposition
            )
          : { requestsConfig, limitsConfig };
        if (firstModelOptions.firstModelDeploymentComposition) {
          firstModelOptions.requests = requests;
          firstModelOptions.limits = limits;
        }
        if (secondModelOptions.secondModelDeploymentComposition) {
          secondModelOptions.requests = requests;
          secondModelOptions.limits = limits;
        }
        if (computeRequestsLimits) {
          setRequestsConfig(requests);
          setLimitsConfig(limits);
        }

        onChangeOptions({ ...firstModelOptions, ...secondModelOptions } as DeploymentOptions);
      },
      [
        artifactCompositionOptionsA,
        artifactCompositionOptionsB,
        selectedModels,
        selectedArtifactCompositionA,
        selectedArtifactCompositionB,
        firstSelectedModelVersionExperimentId,
        secondSelectedModelVersionExperimentId,
        replicas,
        requestsConfig,
        limitsConfig,
        envVariablesConfig,
      ]
    ),
    listVersionsForAllModels = React.useCallback(async () => {
      if (!registeredModelItems) return;
      const allModelVersions: RegisteredModelVersions = {};
      // TODO: Set loader, but not when registeredModelItems are fetched using filter. Check for all filter calls where update of items causes subsequent calls setting loaders.
      loadStateRef.current.getRegisteredModelVersion = true;
      for (const item of registeredModelItems) {
        const modelVersions = await getModelVersionsByModel(item.id, item.displayName);
        if (modelVersions) allModelVersions[item.id] = modelVersions;
      }
      loadStateRef.current.getRegisteredModelVersion = false;
      evaluateLoading();
      // TODO: Sort by first model version creation time as fetch calls may not be in order.
      setRegisteredModelVersions(allModelVersions);
    }, [registeredModelItems]),
    searchProps = React.useMemo(
      () => ({
        emptyMessage: 'No results found',
        hasSearchResult: true,
        onRenderSearchResultItemActions: () => <></>,
        onSearchTextChange: (text?: string) => setSearchFilter(text),
        placeholder: `Search by model name`,
        minCalloutWidth: deploymentType === 'single_model' ? 452 : 280,
        searchResultItemFields: {
          titleField: 'displayName',
          descriptionField: 'description',
        },
        onDismissCallout: () => setSearchFilter(undefined),
        readOnly: !isNew || readOnly,
        actions: permissions.hasAccessToModelsPage ? (
          <Link
            onClick={() => {
              history.push(`/mlops/${workspaceName}${ROUTES.MODELS}`);
            }}
            style={{ color: 'var(--h2o-gray500, #6B7280)' }}
          >
            Manage models
          </Link>
        ) : undefined,
        styles: {
          root: {
            width: '100%',
          },
        },
        // TODO: Add loader.
        loadingMessage: shouldFetchRegisteredModels && registeredModelsQuery.isFetching ? 'Loading models...' : '',
      }),
      [deploymentType, readOnly, permissions, workspaceName, registeredModelsQuery.isFetching, isNew, history]
    ),
    keyValuePairEditorStyles = React.useMemo(
      () => ({
        root: {
          backgroundColor: theme.semanticColors?.contentBackground,
          maxWidth: 460,
          border: `1px solid ${theme.semanticColors?.inputBorder}`,
          borderRadius: 4,
        },
      }),
      [theme.semanticColors?.contentBackground, theme.semanticColors?.inputBorder]
    );

  React.useEffect(() => {
    discoverKubernetesConfigShortcuts();
  }, [discoverKubernetesConfigShortcuts]);

  React.useEffect(() => {
    if (!kubernetesMaxReplicasQuery.isLoading) {
      loadStateRef.current.fetchingKubernetesDeploymentMaxReplicas = false;
      evaluateLoading();
    }
  }, [kubernetesMaxReplicasQuery.isLoading, evaluateLoading]);

  React.useEffect(() => {
    setAffinity(defaultAffinity);
  }, [defaultAffinity]);

  React.useEffect(() => {
    setToleration(defaultToleration);
  }, [defaultToleration]);

  React.useEffect(() => {
    if (registeredModelItems && permissions?.canListModelVersions) {
      void listVersionsForAllModels();
    }
  }, [registeredModelItems, permissions?.canListModelVersions]);

  React.useEffect(() => {
    const hasFirstModelArtifactCompositionsReady = selectedArtifactCompositionA && artifactCompositionOptionsA;
    const hasSecondModelArtifactCompositionsReady = selectedArtifactCompositionB && artifactCompositionOptionsB;
    const hasBothModelsArtifactCompositionsReady =
      hasFirstModelArtifactCompositionsReady && hasSecondModelArtifactCompositionsReady;
    if (
      (deploymentType === 'single_model' && hasFirstModelArtifactCompositionsReady) ||
      (deploymentType !== 'single_model' && hasBothModelsArtifactCompositionsReady)
    ) {
      const hasArtifactCompositionAChanged =
          (defaultDeploymentCompositionA
            ? `${defaultDeploymentCompositionA?.deployableArtifactType || ''}-${
                defaultDeploymentCompositionA?.runtime || ''
              }`
            : undefined) !== selectedArtifactCompositionA,
        hasArtifactCompositionBChanged =
          (defaultDeploymentCompositionB
            ? `${defaultDeploymentCompositionB?.deployableArtifactType || ''}-${
                defaultDeploymentCompositionB?.runtime || ''
              }`
            : undefined) !== selectedArtifactCompositionB,
        shouldComputeRequestsAndLimits =
          isNew || (!isNew && (hasArtifactCompositionAChanged || hasArtifactCompositionBChanged));
      updateOptions(shouldComputeRequestsAndLimits);
    }
  }, [
    selectedArtifactCompositionA,
    selectedArtifactCompositionB,
    defaultDeploymentCompositionA,
    defaultDeploymentCompositionB,
    artifactCompositionOptionsA,
    artifactCompositionOptionsB,
    deploymentType,
    isNew,
  ]);

  React.useEffect(() => {
    if (registeredModelVersions) {
      if (selectedModels?.[0]) {
        const firstModelVersions = registeredModelVersions?.[selectedModels[0]?.id];
        setFirstSelectedModelVersionOptions(
          firstModelVersions?.map((version) => ({
            key: version.experimentId,
            text: `v${version.version}`,
          })) || []
        );
        if (firstModelVersions?.length) {
          const defaultModelVersion =
            defaultSelectedVersionId && !isDefaultSelectionInitializedRef.current.isModelVersionInitialized
              ? firstModelVersions?.find((version) => version.id === defaultSelectedVersionId)
              : undefined;
          if (!isDefaultSelectionInitializedRef.current.isModelVersionInitialized && defaultSelectedVersionId) {
            isDefaultSelectionInitializedRef.current.isModelVersionInitialized = true;
          }
          const latestFirstModelVersion =
            defaultModelVersion ||
            firstModelVersions?.reduce((max, current) => (current.version > max.version ? current : max));

          setFirstSelectedModelVersionExperimentId(
            defaultSelectedExperimentIds?.[0] || latestFirstModelVersion.experimentId
          );
        }
      }
      if (selectedModels?.[1]) {
        const secondModelVersions = registeredModelVersions?.[selectedModels[1]?.id];
        setSecondSelectedModelVersionOptions(
          secondModelVersions?.map((version) => ({
            key: version.experimentId,
            text: `v${version.version}`,
          })) || []
        );
        if (secondModelVersions?.length) {
          const latestSecondModelVersion = secondModelVersions?.reduce((max, current) =>
            current.version > max.version ? current : max
          );
          setSecondSelectedModelVersionExperimentId(
            defaultSelectedExperimentIds?.[1] || latestSecondModelVersion.experimentId
          );
        }
      }
    }
  }, [registeredModelVersions, selectedModels, defaultSelectedVersionId, defaultSelectedExperimentIds]);

  React.useEffect(() => {
    // Select model with its version when quick deploy action is used.
    if (
      defaultSelectedVersionId &&
      registeredModelVersions &&
      !isDefaultSelectionInitializedRef.current.isModelSelectionInitialized
    ) {
      const defaultModelId = Object.entries(registeredModelVersions).find(([_key, value]) =>
        value.some((version) => version.id === defaultSelectedVersionId)
      )?.[0];
      if (defaultModelId) {
        const defaultModel = registeredModelItems?.find((model) => model.id === defaultModelId);
        if (defaultModel) {
          onSelectedModelsChange([defaultModel, selectedModels?.[1]]);
        }
      }
      isDefaultSelectionInitializedRef.current.isModelSelectionInitialized = true;
    }
  }, [defaultSelectedVersionId, registeredModelVersions]);

  React.useEffect(() => {
    if (defaultDeploymentCompositionA) {
      setSelectedArtifactCompositionA(
        defaultDeploymentCompositionA
          ? `${defaultDeploymentCompositionA?.deployableArtifactType || ''}-${
              defaultDeploymentCompositionA?.runtime || ''
            }`
          : ''
      );
    }
    if (defaultDeploymentCompositionB) {
      setSelectedArtifactCompositionB(
        defaultDeploymentCompositionB
          ? `${defaultDeploymentCompositionB?.deployableArtifactType || ''}-${
              defaultDeploymentCompositionB?.runtime || ''
            }`
          : ''
      );
    }
  }, [defaultDeploymentCompositionA, defaultDeploymentCompositionB]);

  React.useEffect(() => {
    if (defaultRequestsConfig) {
      setRequestsConfig(defaultRequestsConfig);
    }
    if (defaultLimitsConfig) {
      setLimitsConfig(defaultLimitsConfig);
    }
    if (defaultReplicas !== undefined) {
      setReplicas(defaultReplicas);
    }
    if (defaultEnvVariablesConfig) {
      setEnvVariablesConfig(defaultEnvVariablesConfig);
    }
  }, [defaultRequestsConfig, defaultLimitsConfig, defaultReplicas, defaultEnvVariablesConfig]);

  React.useEffect(() => {
    // Makes sure, requests, limits and replicas and env variables are propagated to the parent component.
    if (selectedModels?.[0] || selectedModels?.[1]) updateOptions(false);
  }, [requestsConfig, limitsConfig, replicas, envVariablesConfig, selectedModels]);

  // TODO: Update directly from model selection callout.
  React.useEffect(() => {
    onSelectModelUpdateOptions();
  }, [selectedModels, onSelectModelUpdateOptions]);

  React.useEffect(() => onContentLoadStart?.(), []);

  React.useEffect(() => {
    // TODO: For some reason it is called sooner than expected.
    if (!isLoading) onContentLoadFinish?.();
  }, [isLoading]);

  React.useEffect(() => {
    // Collapses advanced settings when model is unselected.
    if (!selectedModels?.[0]) {
      setAdvancedModelsExpanded(false);
    }
  }, [selectedModels]);

  React.useEffect(() => {
    onChangeModelSelectorAdvancedExpanded?.(isAdvancedModelsExpanded);
  }, [isAdvancedModelsExpanded]);

  React.useEffect(() => {
    if (!registeredModelItems || !defaultSelectedModelIds || !defaultSelectedModelIds.length) return;
    const models =
      registeredModelItems
        ?.sort((a, b) => {
          // Sort by order of experiments in existing deployment.
          return (defaultSelectedModelIds?.indexOf(a.id) || 0) - (defaultSelectedModelIds?.indexOf(b.id) || 0);
        })
        .filter((item) => defaultSelectedModelIds?.includes(item.id)) || [];
    if (defaultSelectedModelIds?.[0] === defaultSelectedModelIds?.[1]) onSelectedModelsChange([models[0], models[0]]);
    else onSelectedModelsChange(models);
  }, [defaultSelectedModelIds, registeredModelItems]);

  React.useEffect(() => {
    if (!registeredModelsQuery.isLoading) {
      loadStateRef.current.fetchingRegisteredModels = false;
      evaluateLoading();
    }
  }, [registeredModelsQuery.isLoading, evaluateLoading]);

  // TODO: Make the model widget to start loading with the deployment detail page.
  return (
    <>
      <div className={classNames.widgetLayout}>
        {isLoading ? (
          <div className={classNames.loader}>
            <Loader label="Loading model widget..." />
          </div>
        ) : (
          <>
            {selectedModels?.[0] ? (
              <WidgetItem
                styles={widgetItemStyles(theme)}
                style={{ width: deploymentType === 'single_model' ? '100%' : '50%' }}
                actions={getWidgetActions(
                  () => {
                    window.open(`/mlops/${workspaceName}${ROUTES.MODELS}/${selectedModels[0]?.id}`, '_blank');
                  },
                  () => {
                    onSelectedModelsChange([undefined, selectedModels?.[1]]);
                    setSelectedArtifactCompositionA('');
                    if (deploymentType === 'ab_test') {
                      onTrafficDistributionChange?.(50);
                    }
                    setFirstSelectedModelVersionExperimentId('');
                    setFirstSelectedModelVersionOptions([]);
                    setArtifactCompositionOptionsA([]);
                  },
                  () => {
                    history.push(
                      `/mlops/workspaces/${selectedModels?.[0]?.projectId}${ROUTES.MODELS}/${selectedModels?.[0]?.id}/add-new-version`,
                      { from: 'model-selector' }
                    );
                  },
                  classNames,
                  setFirstSelectedModelVersionExperimentId,
                  firstSelectedModelVersionOptions,
                  firstSelectedModelVersionExperimentId,
                  !isNew || readOnly
                )}
                data={selectedModels[0] || defaultWidgetData}
                idField={'id'}
                titleField={'displayName'}
              />
            ) : (
              <div
                className={mergeStyles(
                  classNames.placeholderContainer,
                  deploymentType === 'single_model' ? '' : classNames.placeholderContainerSmall
                )}
              >
                <div
                  className={mergeStyles(
                    classNames.placeholderContent,
                    deploymentType === 'single_model' ? '' : classNames.placeholderContentSmall
                  )}
                >
                  <p>
                    {deploymentType === 'single_model'
                      ? 'Select a model.'
                      : deploymentType === 'ab_test'
                      ? 'Select Model A.'
                      : 'Select a CHAMPION model.'}
                  </p>
                  <Search
                    {...searchProps}
                    searchResultItems={registeredModelItems}
                    calloutProps={{ dismissOnTargetClick: true, ref: calloutARef }}
                    onClickItem={(item) => {
                      onSelectedModelsChange([item as RegisteredModel, selectedModels?.[1]]);
                      // HACK: Dismiss callout.
                      calloutARef.current?.parentElement?.click();
                    }}
                  />
                </div>
              </div>
            )}
            {shouldShowTwoWidgets ? (
              selectedModels?.[1] ? (
                <WidgetItem
                  styles={widgetItemStyles(theme)}
                  style={{ width: '50%' }}
                  actions={getWidgetActions(
                    () => {
                      window.open(`/mlops/${workspaceName}${ROUTES.MODELS}/${selectedModels[1]?.id}`, '_blank');
                    },
                    () => {
                      onSelectedModelsChange([selectedModels[0], undefined]);
                      setSelectedArtifactCompositionB('');
                      if (deploymentType === 'ab_test') {
                        onTrafficDistributionChange?.(50);
                      }
                      setSecondSelectedModelVersionExperimentId('');
                      setSecondSelectedModelVersionOptions([]);
                      setArtifactCompositionOptionsB([]);
                    },
                    () => {
                      history.push(
                        `/mlops/workspaces/${selectedModels?.[1]?.projectId}${ROUTES.MODELS}/${selectedModels?.[1]?.id}/add-new-version`,
                        { from: 'model-selector' }
                      );
                    },
                    classNames,
                    setSecondSelectedModelVersionExperimentId,
                    secondSelectedModelVersionOptions,
                    secondSelectedModelVersionExperimentId,
                    !isNew || readOnly
                  )}
                  data={selectedModels[1] || defaultWidgetData}
                  idField={'id'}
                  titleField={'displayName'}
                />
              ) : (
                <div className={mergeStyles(classNames.placeholderContainer, classNames.placeholderContainerSmall)}>
                  <div className={mergeStyles(classNames.placeholderContent, classNames.placeholderContentSmall)}>
                    <p>{deploymentType === 'ab_test' ? 'Select Model B.' : 'Select a CHALLENGER model.'}</p>
                    <Search
                      {...searchProps}
                      searchResultItems={registeredModelItems}
                      calloutProps={{ dismissOnTargetClick: true, ref: calloutBRef }}
                      onClickItem={(item) => {
                        onSelectedModelsChange([selectedModels[0], item as RegisteredModel]);
                        // HACK: Dismiss callout.
                        calloutBRef.current?.parentElement?.click();
                      }}
                    />
                  </div>
                </div>
              )
            ) : null}
          </>
        )}
      </div>
      {firstSelectedModelVersionExperimentId === secondSelectedModelVersionExperimentId &&
      firstSelectedModelVersionExperimentId &&
      secondSelectedModelVersionExperimentId ? (
        <Info
          iconStyles={{ root: { color: theme.palette?.red500 } }}
          style={{ marginTop: 10, marginBottom: 10 }}
          styles={{
            text: { alignContent: 'center', color: theme.palette?.red500 },
          }}
        >
          You have selected the same model version for both models. Please select different versions for each model.
        </Info>
      ) : null}
      {((deploymentType === 'single_model' && countSelectedModels(selectedModels) > 0) ||
        (deploymentType !== 'single_model' && countSelectedModels(selectedModels) > 1)) && (
        <>
          <h4>Deployment parameters</h4>
          {selectedModels?.[0] ? (
            <>
              {deploymentType !== 'single_model' ? (
                <h5>{`${deploymentType === 'ab_test' ? 'Model A' : 'Champion model'}`}</h5>
              ) : null}
              <Dropdown
                label="Artifact type & Runtime"
                required
                options={artifactCompositionOptionsA}
                selectedKey={selectedArtifactCompositionA}
                onChange={(_ev, option) => {
                  setSelectedArtifactCompositionA(option?.key as string);
                }}
                disabled={
                  !artifactCompositionOptionsA.length || !countSelectedModels(selectedModels) || !isNew || readOnly
                }
                styles={{ root: { maxWidth: FORM_SECTION_MAX_WIDTH } }}
              />
              {!artifactCompositionOptionsA.length && !isLoading ? (
                <p className={classNames.errorText}>There are no available runtimes present to deploy this model.</p>
              ) : null}
            </>
          ) : null}
          {deploymentType !== 'single_model' && selectedModels?.[1] ? (
            <>
              <h5>{`${deploymentType === 'ab_test' ? 'Model B' : 'Challenger model'}`}</h5>
              <Dropdown
                label="Artifact type & Runtime"
                required
                options={artifactCompositionOptionsB}
                selectedKey={selectedArtifactCompositionB}
                onChange={(_ev, option) => {
                  setSelectedArtifactCompositionB(option?.key as string);
                }}
                disabled={
                  !artifactCompositionOptionsB.length || !countSelectedModels(selectedModels) || !isNew || readOnly
                }
                styles={{ root: { maxWidth: FORM_SECTION_MAX_WIDTH } }}
              />
              {!artifactCompositionOptionsB.length && !isLoading ? (
                <p className={classNames.errorText}>There are no available runtimes present to deploy this model.</p>
              ) : null}
            </>
          ) : null}
          {selectedModels?.[0] && selectedModels?.[1] && deploymentType === 'ab_test' ? (
            <>
              <TooltipLabel
                label="Traffic distribution"
                tooltipText="Configure the distribution of incoming scoring requests to each model."
              />
              <Slider
                value={trafficDistribution}
                min={1}
                max={99}
                valueFormat={(value) => `${value}%`}
                onChange={(value) => onTrafficDistributionChange?.(value)}
                showValue={false}
                step={1}
                disabled={!isNew || readOnly}
                styles={{
                  root: {
                    paddingTop: 0,
                  },
                }}
                sliderContainerStyles={sliderContainerStyles}
              />
              <div className={classNames.sliderLabel}>
                <div
                  className={classNames.sliderLabelLeft}
                >{`${trafficDistribution}% Model A (${selectedModels[0]?.displayName}) `}</div>
                <div className={classNames.sliderLabelRight}>{` ${100 - trafficDistribution}% Model B (${
                  selectedModels[1]?.displayName
                })`}</div>
              </div>
            </>
          ) : null}
          <div style={{ height: 12 }}></div>
          <Expander
            label="Advanced settings"
            onChangeExpanded={(isExpanded) => setAdvancedModelsExpanded(isExpanded)}
          />
          <span
            // Hides form istead of removing it to keep form data for submission when advanced settings are collapsed.
            style={
              isAdvancedModelsExpanded
                ? { marginLeft: 31, marginTop: 20, display: 'block' }
                : { visibility: 'hidden', height: 0, overflow: 'hidden', display: 'block' }
            }
          >
            <>
              <h4>Resource configurations</h4>
              {deploymentType !== 'single_model' ? (
                <Info style={{ marginTop: 10, marginBottom: 10 }} styles={{ text: { alignContent: 'center' } }}>
                  The below resource configurations will be applied for each individual model in the deployment.
                </Info>
              ) : null}
              {/* HACK: Render slider after load as it does not react on value changed externally.  */}
              {/* TODO: Remove? {isNew || (!isNew && !loading) ? ( */}
              {!isLoading ? (
                <Slider
                  label="Replicas"
                  value={replicas}
                  min={0}
                  max={maxReplicas !== undefined ? maxReplicas : 0}
                  onChange={(value) => setReplicas(Math.round(value))}
                  step={1}
                  disabled={readOnly}
                  sliderContainerStyles={replicasSliderContainerStyles}
                />
              ) : null}
              {replicas === 0 ? (
                <Info style={{ marginTop: 10 }} styles={{ text: { alignContent: 'center' } }}>
                  With replicas set to 0, the deployment is not available for scoring.
                </Info>
              ) : null}
              <p className={classNames.formTitle}>Requests</p>
              <KeyValuePairEditor
                config={requestsConfig}
                onUpdateConfig={(newConfig) => setRequestsConfig(newConfig)}
                styles={keyValuePairEditorStyles}
                readOnly={readOnly}
                directlyEditable
              />
              <p className={classNames.formTitle}>Limits</p>
              <KeyValuePairEditor
                config={limitsConfig}
                onUpdateConfig={(newConfig) => setLimitsConfig(newConfig)}
                styles={keyValuePairEditorStyles}
                readOnly={readOnly}
                directlyEditable
              />
              <p className={classNames.formTitle}>Environment variables</p>
              <KeyValuePairEditor
                config={envVariablesConfig}
                onUpdateConfig={(newConfig) => setEnvVariablesConfig(newConfig)}
                styles={keyValuePairEditorStyles}
                readOnly={readOnly}
                directlyEditable
              />
              <div style={{ height: 8 }} />
              <Dropdown
                label="Affinity"
                selectedKey={affinity}
                onChange={(_ev, item) => {
                  const newKey = item?.key as string;
                  setAffinity(newKey);
                  onAffinityChange(newKey);
                }}
                options={[{ key: 'none', text: 'None' }, ...(kubernetesConfigurationShortcutsOptions?.affinity || [])]}
                disabled={readOnly}
                styles={{ root: { maxWidth: 360 } }}
              />
              <div style={{ height: 8 }} />
              <Dropdown
                label="Toleration"
                selectedKey={toleration}
                onChange={(_ev, item) => {
                  const newKey = item?.key as string;
                  setToleration(newKey);
                  onTolerationChange(newKey);
                }}
                options={[
                  { key: 'none', text: 'None' },
                  ...(kubernetesConfigurationShortcutsOptions?.toleration || []),
                ]}
                disabled={readOnly}
                styles={{ root: { maxWidth: 360 } }}
              />
            </>
          </span>
        </>
      )}
    </>
  );
};

export default ModelSelector;
